import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import IconButton from '@mui/material/IconButton';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

import './ImageSelectDialog.css';
import HoverPreview from './HoverPreview';
import ImageSelectStats from './ImageSelectStats';
import CardImage, { CardImageError, TileState } from './CardImage';

import CropImageOverlay from '../../container/CropImageOverlay';
import ChoiceButton from '../../container/inputs/ChoiceButton';
import { onlyThisHint } from '../../container/inputs/useLongPress';
import LanguageFlag from '../../container/LanguageFlag';
import SortableList, { PriorityRank } from '../../container/inputs/SortableList';
import { applyOrder, priorityRank, sortByPriority } from '../filterPriority';
import { imageLoaderService } from "../../service/cache/ImageLoaderService";
import { CardResult, GroupedResults } from '../../api/types/types';

const NO_LANGUAGE = 'No Language';

// Druckschärfe-Filter. Noch nicht gemessene Bilder (nicht geladen) und "kein Text erkannt"
// laufen unter "unrated", damit sie standardmäßig sichtbar bleiben und überhaupt gemessen werden.
type QualityFilter = 'good' | 'ok' | 'poor' | 'unrated';
const QUALITY_FILTERS: {
  key: QualityFilter;
  labelKey: 'imageSelect.quality.good' | 'imageSelect.quality.ok' | 'imageSelect.quality.poor' | 'imageSelect.quality.unrated';
}[] = [
  { key: 'good', labelKey: 'imageSelect.quality.good' },
  { key: 'ok', labelKey: 'imageSelect.quality.ok' },
  { key: 'poor', labelKey: 'imageSelect.quality.poor' },
  { key: 'unrated', labelKey: 'imageSelect.quality.unrated' },
];

const qualityFilterOf = (result: CardResult): QualityFilter => {
  const rating = result.imageInfo?.quality?.rating;
  return rating === 'good' || rating === 'ok' || rating === 'poor' ? rating : 'unrated';
};

const FilterCount: React.FC<{ count: number }> = ({ count }) => {
  const { t } = useTranslation();
  return (
    <span className={`filter-count ${count === 0 ? 'is-empty' : ''}`} title={t('imageSelect.imageCount', { count })}>{count}</span>
  );
};

const languageOf = (result: CardResult) => result.languageCode || NO_LANGUAGE;


interface ImageSelectDialogProps {
  open: boolean;
  /** Bekommt das gewählte Bild nur, wenn im Dialog eins angeklickt wurde, sonst null */
  onClose: (selectedImage: CardResult | null) => void;
  groupedResults: GroupedResults;
  initialSelectedImage: CardResult | null | undefined;
  onImageLoadError: (card: CardImageError) => void;
  initialVisibleLanguages: Set<string>;
  initialVisibleApis: Set<string>;
  /** Seitenverhältnis einer Karte (Breite / Höhe) aus den PDF-Einstellungen. */
  cardAspect?: number;
  /** Filter-Priorität der Hauptseite (oben gewinnt), im Dialog sortierbar */
  languageOrder?: string[];
  apiOrder?: string[];
  onReorderLanguages?: (order: string[]) => void;
  onReorderApis?: (order: string[]) => void;
  /** Blättern zur vorherigen/nächsten Karte im Deck, ohne den Dialog zu schließen */
  deckNav?: DeckNav;
}

/** Position der aktuellen Karte im Deck. Blättern übergibt die Wahl dieser Karte (oder null, wenn nichts geklickt wurde). */
export interface DeckNav {
  /** Ändert sich mit jeder Karte, damit die Auswahl im Dialog zurückgesetzt wird */
  key: string;
  cardName: string;
  /** 1-basiert */
  position: number;
  total: number;
  onStep: (delta: -1 | 1, picked: CardResult | null) => void;
}

const noReorder = () => undefined;

const ImageSelectDialog: React.FC<ImageSelectDialogProps> = ({
  open,
  onClose,
  groupedResults,
  initialSelectedImage,
  onImageLoadError,
  initialVisibleLanguages,
  initialVisibleApis,
  cardAspect,
  languageOrder = [],
  apiOrder = [],
  onReorderLanguages = noReorder,
  onReorderApis = noReorder,
  deckNav }) => {
  const { t } = useTranslation();
  const [selectedImage, setSelectedImage] = useState<CardResult | null>(initialSelectedImage || null);
  // Nur ein Klick auf ein Bild ist eine manuelle Wahl, bloßes Öffnen und Schließen nicht
  const [picked, setPicked] = useState(false);

  const [dialogVisibleLanguages, setDialogVisibleLanguages] = useState(initialVisibleLanguages);
  const [dialogVisibleApis, setDialogVisibleApis] = useState(initialVisibleApis);
  const [visibleQualities, setVisibleQualities] = useState<Set<QualityFilter>>(
    () => new Set(QUALITY_FILTERS.map(f => f.key))
  );
  // Schärfe kommt asynchron nach dem Laden; neu filtern, sobald sich eine Bewertung ändert
  const [, setQualityVersion] = useState(0);
  // const imageLoaderCacheSize = useImageLoaderCacheSize();

  const availableLanguages = useMemo(() => {
    const languages = new Set([NO_LANGUAGE]);
    Object.values(groupedResults).forEach(results => {
      results.forEach(result => {
        if (result.languageCode) {
          languages.add(result.languageCode);
        }
      });
    });
    return languages;
  }, [groupedResults]);
  const orderedLanguages = applyOrder(languageOrder, availableLanguages);
  const orderedApis = applyOrder(apiOrder, Object.keys(groupedResults));

  // Zähler je Filterwert: wie viele Bilder zu sehen wären, wenn der Wert an ist.
  // Die jeweils anderen Filtergruppen gelten mit, die eigene Gruppe nicht.
  // Ohne useMemo, weil die Schärfe-Bewertung erst nach dem Laden in die Ergebnisse geschrieben wird.
  const languageCounts = new Map<string, number>();
  const apiCounts = new Map<string, number>();
  const qualityCounts = new Map<QualityFilter, number>();
  const increment = <K,>(counts: Map<K, number>, key: K) => counts.set(key, (counts.get(key) ?? 0) + 1);
  Object.entries(groupedResults).forEach(([apiName, results]) => {
    const apiVisible = dialogVisibleApis.has(apiName);
    results.forEach(result => {
      const language = result.languageCode || NO_LANGUAGE;
      const quality = qualityFilterOf(result);
      const languageVisible = dialogVisibleLanguages.has(language);
      const qualityVisible = visibleQualities.has(quality);
      if (apiVisible && qualityVisible) increment(languageCounts, language);
      if (languageVisible && qualityVisible) increment(apiCounts, apiName);
      if (apiVisible && languageVisible) increment(qualityCounts, quality);
    });
  });

  const handleImageLoad = (result: CardResult, imageInfo: CardResult['imageInfo']) => {
    const before = qualityFilterOf(result);
    Object.assign(result, { imageInfo: imageInfo });
    if (qualityFilterOf(result) !== before) setQualityVersion(v => v + 1);
  };

  const toggleQuality = (key: QualityFilter) => {
    setVisibleQualities(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // Take over the filters from the main page each time the dialog opens
  useEffect(() => {
    if (open) {
      setDialogVisibleLanguages(new Set(initialVisibleLanguages));
      setDialogVisibleApis(new Set(initialVisibleApis));
      setPicked(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (initialSelectedImage) {
      setSelectedImage(initialSelectedImage);
    }
  }, [initialSelectedImage]);

  // Beim Blättern zur nächsten Karte: Auswahl und "gewählt"-Merker gehören zur neuen Karte
  useEffect(() => {
    setSelectedImage(initialSelectedImage || null);
    setPicked(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deckNav?.key]);

  // Eine manuelle Wahl wird beim Blättern mitgegeben, sonst bleibt die Karte unverändert
  const stepTo = useCallback((delta: -1 | 1) => {
    if (!deckNav) return;
    const target = deckNav.position - 1 + delta;
    if (target < 0 || target >= deckNav.total) return;
    deckNav.onStep(delta, picked ? selectedImage : null);
  }, [deckNav, picked, selectedImage]);

  // Pfeiltasten blättern, außer in Eingabefeldern (z.B. Zahlenfelder im Zuschnitt)
  useEffect(() => {
    if (!open || !deckNav) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) return;
      const target = e.target as HTMLElement | null;
      if (target && (target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName))) return;
      if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
        e.preventDefault();
        stepTo(e.key === 'ArrowLeft' ? -1 : 1);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, deckNav, stepTo]);

  const toggleLanguage = (languageCode: string) => {
    setDialogVisibleLanguages(prev => {
      const newSet = new Set(prev);
      if (newSet.has(languageCode)) {
        newSet.delete(languageCode);
      } else {
        newSet.add(languageCode);
      }
      return newSet;
    });
  };

  const toggleApi = (apiName: string) => {
    setDialogVisibleApis(prev => {
      const newSet = new Set(prev);
      if (newSet.has(apiName)) {
        newSet.delete(apiName);
      } else {
        newSet.add(apiName);
      }
      return newSet;
    });
  };

  const handleImageLoadError = (card: CardImageError) => {
    onImageLoadError(card);
  };

  const handleImageSelect = (image: CardResult) => {
    setSelectedImage(image);
    setPicked(true);
  };

  // Zustand jeder sichtbaren Kachel, Schlüssel ist die Position im Grid (wie beim Rendern)
  const [tileStates, setTileStates] = useState<Map<string, TileState>>(() => new Map());
  const setTileState = useCallback((tileId: string, state: TileState) => {
    setTileStates(prev => {
      if (state === null) {
        if (!prev.has(tileId)) return prev;
        const next = new Map(prev);
        next.delete(tileId);
        return next;
      }
      if (prev.get(tileId) === state) return prev;
      return new Map(prev).set(tileId, state);
    });
  }, []);

  const tileCounts = useMemo(() => {
    const counts: Record<'loading' | 'queued' | 'done', number> = { loading: 0, queued: 0, done: 0 };
    tileStates.forEach(state => {
      if (state === 'loading' || state === 'queued' || state === 'done') counts[state]++;
    });
    return counts;
  }, [tileStates]);

  const cardName = useMemo(
    () => initialSelectedImage?.name ?? Object.values(groupedResults).flat()[0]?.name,
    [initialSelectedImage, groupedResults]
  );

  const handleClose = () => {
    imageLoaderService.stopByCategory('selectgrid');
    onClose(picked ? selectedImage : null);
  };

  if (!open) return null;

  return (
    <div className="image-select-dialog">
      <div className="dialog-overlay" onClick={handleClose}>
        <div className="dialog-content" onClick={e => e.stopPropagation()}>
          <ImageSelectStats open={open} cardName={cardName} counts={tileCounts} />
          <div className="image-selection-container">
            {deckNav && (
              <div className="deck-nav">
                <IconButton
                  aria-label={t('imageSelect.previousCard')}
                  title={t('imageSelect.previousCardHint')}
                  disabled={deckNav.position <= 1}
                  onClick={() => stepTo(-1)}
                >
                  <ChevronLeftIcon />
                </IconButton>
                <span className="deck-nav-label">{deckNav.cardName} · {deckNav.position} / {deckNav.total}</span>
                <IconButton
                  aria-label={t('imageSelect.nextCard')}
                  title={t('imageSelect.nextCardHint')}
                  disabled={deckNav.position >= deckNav.total}
                  onClick={() => stepTo(1)}
                >
                  <ChevronRightIcon />
                </IconButton>
              </div>
            )}
            <CropImageOverlay
              selectedImage={selectedImage}
              cardAspect={cardAspect}
            />
          </div>
          <div className="image-grid-container">
            {orderedApis
              .filter(apiName => dialogVisibleApis.has(apiName))
              .map(apiName => {
                // Innerhalb einer API nach Sprach-Priorität, sonst in der Reihenfolge der Suche
                const visibleResults = sortByPriority(groupedResults[apiName].filter(result =>
                  dialogVisibleLanguages.has(languageOf(result)) &&
                  visibleQualities.has(qualityFilterOf(result))
                ), languageOrder, [], languageOf);
                return (
                  <div key={apiName} className={visibleResults.length === 0 ? 'no-matching-card' : ''}>
                    <h3>{apiName}</h3>
                    <div className="image-grid">
                      {visibleResults.map((result, index) => (
                        <HoverPreview
                          key={index}
                          card={result}
                          className={`image-option ${result === selectedImage ? 'selected' : ''}`}
                          onClick={() => handleImageSelect(result)}
                        >
                          <CardImage
                            cardInput={result}
                            onLoad={(imageInfo) => handleImageLoad(result, imageInfo)}
                            onError={handleImageLoadError}
                            onTileState={(state) => setTileState(`${apiName}:${index}`, state)}
                            showImageSettings={true}
                            onlyDimensions={true}
                          />
                        </HoverPreview>
                      ))}
                    </div>
                  </div>
                );
              })}
          </div>
          <div className="filter-controls">
            <div className="language-filter input-tile-group">
              <h3 className="title is-4">{t('imageSelect.filterLanguage')}</h3>
              <SortableList
                items={orderedLanguages}
                onReorder={onReorderLanguages}
                renderItem={(lang, handle) => (
                  <ChoiceButton
                    id={`select-lang-${lang}`}
                    label={
                      <div className="label-container">
                        <span>{lang === NO_LANGUAGE ? t('imageSelect.noLanguage') : lang}</span>
                        <FilterCount count={languageCounts.get(lang) ?? 0} />
                        <LanguageFlag language={lang} />
                      </div>
                    }
                    checked={dialogVisibleLanguages.has(lang)}
                    onChange={() => toggleLanguage(lang)}
                    onLongPress={() => setDialogVisibleLanguages(new Set([lang]))}
                    longPressHint={onlyThisHint()}
                    trailing={handle}
                    badge={<PriorityRank rank={priorityRank(orderedLanguages, dialogVisibleLanguages, lang)} />}
                  />
                )}
              />
            </div>
            <div className="language-filter input-tile-group">
              <h3 className="title is-4">{t('imageSelect.filterApi')}</h3>
              <SortableList
                items={orderedApis}
                onReorder={onReorderApis}
                renderItem={(apiName, handle) => (
                  <ChoiceButton
                    id={`select-api-${apiName}`}
                    label={
                      <div className="label-container">
                        <span>{apiName}</span>
                        <FilterCount count={apiCounts.get(apiName) ?? 0} />
                      </div>
                    }
                    checked={dialogVisibleApis.has(apiName)}
                    onChange={() => toggleApi(apiName)}
                    onLongPress={() => setDialogVisibleApis(new Set([apiName]))}
                    longPressHint={onlyThisHint()}
                    trailing={handle}
                    badge={<PriorityRank rank={priorityRank(orderedApis, dialogVisibleApis, apiName)} />}
                  />
                )}
              />
            </div>
            <div className="language-filter input-tile-group">
              <h3 className="title is-4">{t('imageSelect.filterSharpness')}</h3>
              {QUALITY_FILTERS.map(({ key, labelKey }) => (
                <ChoiceButton
                  key={key}
                  id={`select-quality-${key}`}
                  label={
                    <div className="label-container">
                      <span>{t(labelKey)}</span>
                      <FilterCount count={qualityCounts.get(key) ?? 0} />
                      <span className={`quality-dot-inline quality-${key === 'unrated' ? 'unknown' : key}`} />
                    </div>
                  }
                  checked={visibleQualities.has(key)}
                  onChange={() => toggleQuality(key)}
                  onLongPress={() => setVisibleQualities(new Set([key]))}
                  longPressHint={onlyThisHint()}
                />
              ))}
            </div>
            {/* <div>
              {imageLoaderCacheSize}
            </div> */}
          </div>
        </div>
      </div>
    </div >
  );
};

export default ImageSelectDialog;
